import { useSnackbar } from 'notistack'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { authUserFromDTO } from 'features/user/api/authUser'
import { useAbility } from 'providers/casl'
import { mapGraphQLErrors } from 'providers/tanstack-query/errors'
import { useMutation } from 'providers/tanstack-query/useMutation'
import { useAuthenticate } from '../useAuthenticate'
import { SIGN_IN } from './api/mutation'

import type { Data, Variables } from './api/types'
import type { UseSignIn } from './types'
import type { Schema } from 'features/user/forms/UserLoginForm/schema'
import type { FieldErrors } from 'react-hook-form'

export const useSignIn = (): UseSignIn => {
  const { t } = useTranslation('features/user')
  const { authenticate } = useAuthenticate()
  const { enqueueSnackbar } = useSnackbar()
  const [errors, setErrors] = useState<FieldErrors<Schema>>({})
  const ability = useAbility()

  const { isPending, mutateAsync } = useMutation<Variables, Data>({
    mutation: SIGN_IN(ability),
    onCompleted: async (data: Data) => {
      const {
        signIn: { accessToken, errors, refreshToken, user },
      } = data

      if (errors && errors.length > 0) {
        setErrors(mapGraphQLErrors(errors))
      } else if (accessToken && refreshToken && user) {
        setErrors({})
        authenticate({ accessToken, refreshToken, user: authUserFromDTO(user) })
        enqueueSnackbar(t('login.success'), { variant: 'success' })
      }
    },
  })

  const signIn = (variables: Schema): void => {
    void mutateAsync(variables)
  }

  return {
    errors,
    loading: isPending,
    signIn,
  }
}
